import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const steps = ["About you", "Measurements", "Goal & activity", "Lifestyle", "Health & consent"];

export default function Onboarding() {
  const { refreshMe } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [err, setErr] = useState("");
  const [targets, setTargets] = useState(null);
  const [f, setF] = useState({
    age: 45, sex: "male", height_cm: 178, weight_kg: 105, goal_weight_kg: 90,
    activity_level: "light", goal: "lose_fat",
    food_preferences: "", exercise_experience: "beginner",
    equipment: [], medical_info: "", medical_clearance_needed: false,
    intermittent_fasting: false, consent_health_data: false, consent_privacy_policy: false,
  });
  const upd = (k, v) => setF({ ...f, [k]: v });
  const toggleEq = (e) => setF({ ...f, equipment: f.equipment.includes(e) ? f.equipment.filter(x=>x!==e) : [...f.equipment, e] });

  const submit = async () => {
    setErr("");
    try {
      const { data } = await api.post("/onboarding", f);
      setTargets(data.targets);
      await refreshMe();
    } catch (e) { setErr(formatApiError(e)); }
  };

  if (targets) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white px-5 py-10">
        <div className="max-w-xl mx-auto">
          <div className="text-gold uppercase tracking-[0.3em] text-xs">Your targets</div>
          <h1 className="font-display font-black uppercase text-4xl tracking-tighter mt-2">Here's your fuel plan</h1>
          <p className="text-neutral-400 mt-2 text-sm">Based on Mifflin-St Jeor estimates. Your coach can adjust these.</p>
          <div className="mt-6 grid grid-cols-2 gap-3">
            {[
              ["Calories", `${targets.calories} kcal`],
              ["Protein", `${targets.protein_g} g`],
              ["Carbs", `${targets.carbs_g} g`],
              ["Fat", `${targets.fat_g} g`],
            ].map(([k,v]) => (
              <div key={k} className="kw-card p-4" data-testid={`target-${k.toLowerCase()}`}>
                <div className="text-xs uppercase tracking-widest text-neutral-500">{k}</div>
                <div className="font-display font-black text-3xl mt-1">{v}</div>
              </div>
            ))}
          </div>
          <p className="text-xs text-neutral-500 mt-4">These are estimates, not guarantees. Actual results depend on consistency, sleep, stress and adherence.</p>
          <button className="kw-btn-primary w-full mt-8" onClick={()=>nav("/app/dashboard")} data-testid="onboarding-done">Enter the programme</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white px-5 py-8">
      <div className="max-w-xl mx-auto">
        <div className="flex items-center gap-2 mb-6">
          {steps.map((s, i) => (
            <div key={i} className={`flex-1 h-1.5 ${i<=step ? "bg-gold" : "bg-[#262626]"}`} />
          ))}
        </div>
        <div className="text-gold uppercase tracking-[0.3em] text-xs">Step {step+1} / {steps.length}</div>
        <h1 className="font-display font-black uppercase text-3xl tracking-tighter mt-1">{steps[step]}</h1>

        <div className="mt-6 space-y-4">
          {step === 0 && (
            <>
              <div><label className="kw-label">Age</label><input className="kw-input" type="number" value={f.age} onChange={(e)=>upd("age", parseInt(e.target.value||"0"))} data-testid="ob-age"/></div>
              <div>
                <label className="kw-label">Sex (biological, for calorie calculation)</label>
                <div className="grid grid-cols-2 gap-2">
                  {["male","female"].map(x => (
                    <button key={x} type="button" onClick={()=>upd("sex", x)} className={`py-3 border uppercase tracking-widest text-sm ${f.sex===x?"border-gold text-gold":"border-[#333] text-neutral-300"}`} data-testid={`ob-sex-${x}`}>{x}</button>
                  ))}
                </div>
              </div>
            </>
          )}
          {step === 1 && (
            <>
              <div><label className="kw-label">Height (cm)</label><input className="kw-input" type="number" value={f.height_cm} onChange={(e)=>upd("height_cm", parseFloat(e.target.value||"0"))} data-testid="ob-height"/></div>
              <div><label className="kw-label">Current weight (kg)</label><input className="kw-input" type="number" step="0.1" value={f.weight_kg} onChange={(e)=>upd("weight_kg", parseFloat(e.target.value||"0"))} data-testid="ob-weight"/></div>
              <div><label className="kw-label">Goal weight (kg, optional)</label><input className="kw-input" type="number" step="0.1" value={f.goal_weight_kg||""} onChange={(e)=>upd("goal_weight_kg", parseFloat(e.target.value||"0"))} data-testid="ob-goal-weight"/></div>
            </>
          )}
          {step === 2 && (
            <>
              <div>
                <label className="kw-label">Goal</label>
                <div className="grid grid-cols-2 gap-2">
                  {[["lose_fat","Lose fat"],["build_muscle","Build muscle"],["maintain","Maintain"],["improve_fitness","Improve fitness"]].map(([v,l]) => (
                    <button key={v} type="button" onClick={()=>upd("goal", v)} className={`py-3 border uppercase tracking-widest text-xs ${f.goal===v?"border-gold text-gold":"border-[#333] text-neutral-300"}`} data-testid={`ob-goal-${v}`}>{l}</button>
                  ))}
                </div>
              </div>
              <div>
                <label className="kw-label">Activity level</label>
                <div className="space-y-2">
                  {[["sedentary","Sedentary — desk job, little movement"],["light","Light — 1-3 walks per week"],["moderate","Moderate — 3-5 sessions/week"],["active","Active — 6-7 sessions/week"],["very_active","Very active — physical job + training"]].map(([v,l]) => (
                    <button key={v} type="button" onClick={()=>upd("activity_level", v)} className={`w-full text-left px-3 py-2 border text-sm ${f.activity_level===v?"border-gold text-gold":"border-[#333] text-neutral-300"}`} data-testid={`ob-act-${v}`}>{l}</button>
                  ))}
                </div>
              </div>
            </>
          )}
          {step === 3 && (
            <>
              <div><label className="kw-label">Food preferences / family meals</label><textarea className="kw-input" rows={3} value={f.food_preferences} onChange={(e)=>upd("food_preferences", e.target.value)} data-testid="ob-food-pref"/></div>
              <div>
                <label className="kw-label">Exercise experience</label>
                <div className="grid grid-cols-3 gap-2">
                  {["beginner","intermediate","advanced"].map(x => (
                    <button key={x} type="button" onClick={()=>upd("exercise_experience", x)} className={`py-2 border uppercase text-xs tracking-widest ${f.exercise_experience===x?"border-gold text-gold":"border-[#333] text-neutral-300"}`} data-testid={`ob-exp-${x}`}>{x}</button>
                  ))}
                </div>
              </div>
              <div>
                <label className="kw-label">Equipment available</label>
                <div className="flex flex-wrap gap-2">
                  {["dumbbells","barbell","resistance bands","pull-up bar","gym access","none"].map(x => (
                    <button key={x} type="button" onClick={()=>toggleEq(x)} className={`px-3 py-1.5 border text-xs uppercase ${f.equipment.includes(x)?"border-gold text-gold":"border-[#333] text-neutral-300"}`} data-testid={`ob-eq-${x.replace(/\s/g,'-')}`}>{x}</button>
                  ))}
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm text-neutral-300">
                <input type="checkbox" checked={f.intermittent_fasting} onChange={(e)=>upd("intermittent_fasting", e.target.checked)} data-testid="ob-if"/>
                I'd like to try intermittent fasting (optional, not required)
              </label>
            </>
          )}
          {step === 4 && (
            <>
              <div><label className="kw-label">Relevant medical info (optional)</label><textarea className="kw-input" rows={3} value={f.medical_info} onChange={(e)=>upd("medical_info", e.target.value)} placeholder="E.g. hypertension, diabetes, previous injuries" data-testid="ob-med-info"/></div>
              <label className="flex items-start gap-2 text-sm text-neutral-300">
                <input type="checkbox" className="mt-1" checked={f.medical_clearance_needed} onChange={(e)=>upd("medical_clearance_needed", e.target.checked)} data-testid="ob-med-clear"/>
                I understand I may need GP clearance before starting intense training. I'll share any clearance with my coach.
              </label>
              <label className="flex items-start gap-2 text-sm text-neutral-300">
                <input type="checkbox" className="mt-1" checked={f.consent_health_data} onChange={(e)=>upd("consent_health_data", e.target.checked)} data-testid="ob-consent-health"/>
                I consent to Kaihau Warrior Coaching storing my health information securely for coaching purposes only.
              </label>
              <label className="flex items-start gap-2 text-sm text-neutral-300">
                <input type="checkbox" className="mt-1" checked={f.consent_privacy_policy} onChange={(e)=>upd("consent_privacy_policy", e.target.checked)} data-testid="ob-consent-privacy"/>
                I've read the <a href="/privacy" className="text-gold underline">Privacy Policy</a>.
              </label>
              <p className="text-xs text-neutral-500">This programme is coaching, not medical advice. It does not diagnose or treat conditions. Please consult your GP for any medical concerns.</p>
            </>
          )}
          {err && <div className="text-red-400 text-sm">{err}</div>}
        </div>

        <div className="mt-8 flex gap-3">
          {step > 0 && <button className="kw-btn-outline flex-1" onClick={()=>setStep(step-1)} data-testid="ob-back">Back</button>}
          {step < steps.length - 1 ? (
            <button className="kw-btn-primary flex-1" onClick={()=>setStep(step+1)} data-testid="ob-next">Next</button>
          ) : (
            <button className="kw-btn-primary flex-1" onClick={submit} disabled={!f.consent_health_data || !f.consent_privacy_policy} data-testid="ob-submit">Calculate my plan</button>
          )}
        </div>
      </div>
    </div>
  );
}
